import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import translate from '../../../../../commonComponents/translate';
import BoxIcon from '../../../../../commonComponents/icons/BoxIcon';
import ProductVariationsProp from '../../../../shop/propTypes/productVariationsProp';
import DetailItem from '../../../../shop/detail/DetailItem';
import {
  formatNumbers,
  newFormatPrice,
} from '../../../../../services/utilityFunctions';
import ConfigsContext from '../../../../../contextProviders/ConfigsContext';
import UserProp from '../../../../../commonComponents/propTypes/userProp';
import User from '../../../../../../../../domain/models/user';

const productIsPack = product => product.type === 'pack';

const renderPackInfo = (productItem, packProduct, frequency, t) => {
  const { quantity } = productItem;
  const { name, images } = packProduct || {};
  const image = images[0]?.thumb || '/images/no_image_upload.png';

  return (
    <>
      <img src={image} alt={name} />
      <div className="information">
        <h4 className="product-name">{name}</h4>
        <p className="variation-name">{t('variation.name', name)}</p>
        <p className="variation-quantity">{t('quantity.number', quantity)}</p>
        <span className="badge-pack">{t('shop.order_pack')}</span>
        {frequency ? (
          <p className="variation-frequency">
            {t(`order.subscription.period.${frequency}`)}
          </p>
        ) : null}
      </div>
    </>
  );
};

const renderVariationInfo = (productItem, variation, frequency, t) => {
  const { parentProduct, quantity } = productItem;
  const { name: displayName } = parentProduct || {};
  const { icon, name } = variation || {};
  const image = icon?.thumb || '/images/no_image_upload.png';

  return (
    <>
      <img src={image} alt={name} />
      <div className="information">
        <h4 className="product-name">{displayName}</h4>
        <p className="variation-name">{t('variation.name', name)}</p>
        <p className="variation-quantity">{t('quantity.number', quantity)}</p>
        {frequency ? (
          <p className="variation-frequency">
            {t(`order.subscription.period.${frequency}`)}
          </p>
        ) : null}
      </div>
    </>
  );
};

function ProductInformation(props) {
  const {
    t,
    product: productItem,
    index,
    currency: currencyCode,
    owner,
    onOpenForm,
    frequency,
  } = props;
  const { tenant } = useContext(ConfigsContext);
  const { directSellingSettings, directSelling } = tenant?.settings;
  const { CVIntegration, VAIntegration } = directSellingSettings || {};
  const isOwnerACollaborator =
    owner?.type === User.TYPE.COLLABORATOR || owner?.role === User.ROLE.ADMIN;

  const {
    product: variation,
    packProduct,
    price,
    subtotalTax,
    subtotal,
    formQuestionsData,
    formAnswerData,
    formAnswersLink,
    _id: productId,
  } = productItem || {};

  const formProviderType = variation?.form?.providerType;
  const va = productIsPack(productItem) ? productItem.va : variation.va;
  const cv = productIsPack(productItem) ? productItem.cv : variation.cv;

  // region Price values
  const priceValue = newFormatPrice(price, true, currencyCode);
  const vatValue = newFormatPrice(subtotalTax, true, currencyCode);
  const showVA = directSelling && VAIntegration && isOwnerACollaborator;
  const vaValue = formatNumbers(va, false, true);
  const showCV = directSelling && CVIntegration && isOwnerACollaborator;
  const cvValue = formatNumbers(cv, false, true);
  const subtotalValue = newFormatPrice(subtotal, true, currencyCode);
  // endregion Price values

  return (
    <div className="m-portlet product-container">
      <div className="product-information">
        <div className="header">
          <BoxIcon />
          <h5 className="product-number">{t('product.number', index + 1)}</h5>
        </div>
        <div className="information-container">
          <div className="main-information">
            {productIsPack(productItem)
              ? renderPackInfo(productItem, packProduct, frequency, t)
              : renderVariationInfo(productItem, variation, frequency, t)}
          </div>
          <DetailItem text={priceValue} header={t('price')} />
          <DetailItem text={vatValue} header={t('order_detail_tax')} />
          {showVA ? <DetailItem text={vaValue} header="VA" /> : null}
          {showCV ? <DetailItem text={cvValue} header="CV" /> : null}
          <DetailItem text={subtotalValue} header={t('order_item_subtotal')} />
          {formQuestionsData ? (
            <button
              type="button"
              className="form-open"
              onClick={() =>
                onOpenForm(
                  formProviderType,
                  productId,
                  variation,
                  formQuestionsData,
                  formAnswerData,
                  formAnswersLink,
                )
              }
            >
              {t('open.form')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

ProductInformation.propTypes = {
  t: PropTypes.func.isRequired,
  onOpenForm: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductVariationsProp.propType).isRequired,
  index: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  owner: PropTypes.shape(UserProp.propType).isRequired,
  frequency: PropTypes.string,
};

ProductInformation.defaultProps = {
  frequency: null,
};

export default translate(ProductInformation);
